import type {
  GcpAnalysisResult,
  GcpAnalyzerSectionKey,
  GcpRecommendationsResult,
  GcpRecurringQueryGroup,
  GcpSectionData,
  NewSkuRow,
} from '@/services/gcpCosts'
import { isGcpSectionError } from '@/services/gcpCosts'
import { formatCurrency, formatNumber as formatProjectionNumber } from '@/pages/projections/projectionsFormat'

export interface AnalyzerStep {
  key: GcpAnalyzerSectionKey
  label: string
}

export const ANALYZER_STEPS: readonly AnalyzerStep[] = [
  { key: 'billing', label: 'Billing overview' },
  { key: 'bigquery', label: 'BigQuery' },
  { key: 'cloud_run', label: 'Cloud Run' },
  { key: 'cloud_sql', label: 'Cloud SQL' },
  { key: 'gcs', label: 'Cloud Storage' },
  { key: 'compute', label: 'Compute & Network' },
  { key: 'egress', label: 'Network egress' },
  { key: 'misc', label: 'Everything else' },
] as const

export type GcpTriggerKey =
  | 'sheets_connector'
  | 'scheduled_query'
  | 'ml'
  | 'service_account'
  | 'ad_hoc'
  | string

export interface TriggerChip {
  label: string
  colorClass: string
}

export interface WastePatternChip {
  label: string
  colorClass: string
}

const WASTE_PATTERN_CHIP_MAP: Record<string, WastePatternChip> = {
  metadata_scan: {
    label: 'metadata scan',
    colorClass: 'bg-amber-100 text-amber-800',
  },
  full_rebuild_ctas: {
    label: 'full rebuild',
    colorClass: 'bg-amber-100 text-amber-800',
  },
  uncached_repeat: {
    label: 'uncached',
    colorClass: 'bg-gray-100 text-gray-700',
  },
  unpartitioned_hotspot: {
    label: 'unpartitioned',
    colorClass: 'bg-amber-100 text-amber-800',
  },
}

export function classifyWastePatternChip(pattern: string): WastePatternChip {
  const mapped = WASTE_PATTERN_CHIP_MAP[pattern]
  if (mapped) return mapped
  return {
    label: pattern.replace(/_/g, ' '),
    colorClass: 'bg-gray-100 text-gray-700',
  }
}

const TRIGGER_CHIP_MAP: Record<string, TriggerChip> = {
  sheets_connector: {
    label: 'Sheets connector',
    colorClass: 'bg-green-100 text-green-800',
  },
  scheduled_query: {
    label: 'Scheduled query',
    colorClass: 'bg-blue-100 text-blue-800',
  },
  ml: {
    label: 'Model training / ML',
    colorClass: 'bg-purple-100 text-purple-800',
  },
  service_account: {
    label: 'Service account',
    colorClass: 'bg-orange-100 text-orange-800',
  },
  ad_hoc: {
    label: 'Ad-hoc / console',
    colorClass: 'bg-gray-100 text-gray-700',
  },
}

export function classifyTriggerChip(trigger: string, triggerLabel?: string): TriggerChip {
  const mapped = TRIGGER_CHIP_MAP[trigger]
  if (mapped) {
    return triggerLabel ? { ...mapped, label: triggerLabel } : mapped
  }
  return {
    label: triggerLabel || trigger,
    colorClass: 'bg-gray-100 text-gray-700',
  }
}

/**
 * Accounting-style money format: $#,##0.00;($#,##0.00);-
 * Zero renders as a dash; negatives in parentheses.
 */
export function formatUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return formatCurrency(value)
}

/**
 * Accounting-style number format: #,##0.00;(#,##0.00);-
 * Zero renders as a dash; negatives in parentheses. No currency symbol.
 */
export function formatNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return formatProjectionNumber(value)
}

/** Whole-dollar axis labels: $1,250 — no decimals. */
export function formatAxisUsd(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  if (value === 0) return '$0'
  const rounded = Math.round(value)
  const formatted = Math.abs(rounded).toLocaleString('en-US', {
    maximumFractionDigits: 0,
  })
  return rounded < 0 ? `($${formatted})` : `$${formatted}`
}

/** Compact currency axis labels: $250K — for tight chart margins. */
export function formatAxisUsdCompact(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  if (value === 0) return '$0'
  const rounded = Math.round(value)
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Math.abs(rounded))
  return rounded < 0 ? `(${formatted})` : formatted
}

export function formatPct(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${value.toFixed(digits)}%`
}

/**
 * Build a padded y-axis domain with round tick values (0 … niceMax).
 * Adds ~10% headroom above dataMax before nicening.
 */
export function niceAxisScale(
  dataMax: number,
  tickCount = 5
): { max: number; ticks: number[] } {
  if (!Number.isFinite(dataMax) || dataMax <= 0) {
    return { max: 1, ticks: [0, 0.25, 0.5, 0.75, 1] }
  }

  const niceNum = (range: number, round: boolean): number => {
    const exp = Math.floor(Math.log10(range))
    const frac = range / 10 ** exp
    let niceFrac: number
    if (round) {
      if (frac < 1.5) niceFrac = 1
      else if (frac < 3) niceFrac = 2
      else if (frac < 7) niceFrac = 5
      else niceFrac = 10
    } else if (frac <= 1) niceFrac = 1
    else if (frac <= 2) niceFrac = 2
    else if (frac <= 5) niceFrac = 5
    else niceFrac = 10
    return niceFrac * 10 ** exp
  }

  const padded = dataMax * 1.12
  const roughStep = padded / Math.max(tickCount - 1, 1)
  const step = niceNum(roughStep, true)
  const niceMax = Math.ceil(padded / step) * step
  const ticks: number[] = []
  for (let value = 0; value <= niceMax + step * 1e-9; value += step) {
    ticks.push(Number(value.toPrecision(12)))
  }
  return { max: niceMax, ticks }
}

export const SKU_GLOSSARY: ReadonlyArray<{ match: string; explanation: string }> = [
  {
    match: 'Analysis',
    explanation:
      'What BigQuery charges for the data your queries read. Priced per TB scanned.',
  },
  {
    match: 'Active Logical Storage',
    explanation: 'Storage for data modified in the last 90 days. Priced per GB-month.',
  },
  {
    match: 'Long Term Logical Storage',
    explanation: 'Storage for data not modified in 90+ days. Cheaper than active storage.',
  },
  {
    match: 'Cloud SQL',
    explanation: 'Managed database instance compute and storage charges.',
  },
  {
    match: 'Cloud Run',
    explanation: 'Serverless container compute billed for CPU, memory, and requests.',
  },
  {
    match: 'Networking',
    explanation: 'Egress and network transfer between regions or to the internet.',
  },
  {
    match: 'Compute Engine',
    explanation: 'Virtual machine instance hours, disks, and attached resources.',
  },
  {
    match: 'Storage',
    explanation: 'Object storage for buckets (Standard, Nearline, Coldline, Archive).',
  },
  {
    match: 'Slot',
    explanation:
      'Reserved BigQuery processing capacity. On-demand queries bill per TB instead.',
  },
  {
    match: 'instance-based',
    explanation:
      'Cloud Run bills idle CPU and memory for always-on instances instead of per-request. Appears when min instances > 0 or instance-based allocation is enabled.',
  },
  {
    match: 'Data Transfer Out',
    explanation:
      'Outbound network egress — bytes leaving GCP regions or the internet. Often the largest unexpected cost driver.',
  },
  {
    match: 'Egress',
    explanation:
      'Outbound data transfer charges when bytes leave a service, region, or the public internet.',
  },
  {
    match: 'secret version replica',
    explanation:
      'Secret Manager charges per active secret version per replication location.',
  },
  {
    match: 'object management',
    explanation:
      'Storage Intelligence object management fees for metadata indexing and analytics on bucket objects.',
  },
  {
    match: 'Storage Intelligence',
    explanation:
      'GCS feature that indexes bucket metadata for analytics — billed as an object management fee.',
  },
  {
    match: 'Invoice',
    explanation:
      'Sales tax and other non-usage Invoice line items. Excluded from optimization totals.',
  },
]

export const TAX_SERVICE_NAMES = new Set(['Invoice'])

export function isTaxService(service: string): boolean {
  return TAX_SERVICE_NAMES.has(service)
}

const CLASSIFICATION_MEANINGFUL_DELTA_USD = 0.5

export interface SpendClassification {
  steady: number
  usage_driven: number
  new_or_changed: number
  tax_and_credits: number
  total: number
}

export interface BiggestChange {
  service: string
  deltaUsd: number
  partition: string | null
  onsetDate: string | null
}

export interface RunRateProjection {
  runRateMonthly: number
  projectedMonth: number | null
  runRateLabel: string
  projectedLabel: string | null
}

function parseSpendBridgeRows(billing: Record<string, unknown> | null) {
  const raw = billing?.spend_bridge
  if (!raw || typeof raw !== 'object') return []
  const bridge = raw as Record<string, unknown>
  return asRecordArray(bridge.rows).map((row) => ({
    service: asString(row.service),
    prevMonthUsd: asNumber(row.prev_month_usd) ?? 0,
    currentRunRateUsd: asNumber(row.current_run_rate_usd) ?? 0,
    deltaUsd: asNumber(row.delta_usd) ?? 0,
    partition: asString(row.partition),
    onsetDate: row.onset_date ? asString(row.onset_date) : null,
  }))
}

function parseLevelShiftServices(billing: Record<string, unknown> | null): Set<string> {
  const raw = billing?.what_changed
  if (!raw || typeof raw !== 'object') return new Set()
  const payload = raw as Record<string, unknown>
  const services = asRecordArray(payload.level_shifts).map((row) => asString(row.service))
  return new Set(services.filter(Boolean))
}

function parseNewSkuServices(billing: Record<string, unknown> | null): Set<string> {
  const raw = billing?.what_changed
  if (!raw || typeof raw !== 'object') return new Set()
  const payload = raw as Record<string, unknown>
  const services = asRecordArray(payload.new_skus).map((row) => asString(row.service))
  return new Set(services.filter(Boolean))
}

function parseSteadyStateServices(billing: Record<string, unknown> | null): Set<string> {
  return new Set(
    asRecordArray(billing?.steady_state_services)
      .map((row) => asString(row.service))
      .filter(Boolean)
  )
}

function getTaxMonthlyUsd(
  misc: Record<string, unknown> | null | undefined,
  bridgeRows: ReturnType<typeof parseSpendBridgeRows>
): number {
  let tax = 0
  for (const row of bridgeRows) {
    if (isTaxService(row.service)) tax += row.currentRunRateUsd
  }
  if (tax > 0) return tax

  for (const row of asRecordArray(misc?.tax_items)) {
    tax +=
      asNumber(row.run_rate_monthly_usd) ??
      asNumber(row.monthly_cost_usd) ??
      0
  }
  return tax
}

/**
 * Partition the current monthly run rate into four overview buckets (WS7 / design principle 4).
 *
 * Each non-tax service's `current_run_rate_usd` from `spend_bridge` is assigned to exactly one
 * spend bucket:
 * - **tax_and_credits** — Invoice (tax) run rate from the bridge or misc `tax_items`. Credits
 *   in `credits_breakdown` are already netted into service run rates and shown separately in the
 *   credits table.
 * - **new_or_changed** — level-shift services, `what_changed.new_skus` services, or bridge rows
 *   partitioned as `new_sku` / `grown` with delta ≥ $0.50/mo.
 * - **steady** — services in `steady_state_services` with no level shift.
 * - **usage_driven** — remaining non-tax spend.
 */
export function buildSpendClassification(
  billing: Record<string, unknown> | null,
  misc?: Record<string, unknown> | null
): SpendClassification {
  const bridgeRaw = billing?.spend_bridge
  const bridge =
    bridgeRaw && typeof bridgeRaw === 'object'
      ? (bridgeRaw as Record<string, unknown>)
      : null

  const total =
    asNumber(bridge?.current_run_rate_total_usd) ?? sumRunRatesByService(billing) ?? 0

  const bridgeRows = parseSpendBridgeRows(billing)
  const levelShiftServices = parseLevelShiftServices(billing)
  const newSkuServices = parseNewSkuServices(billing)
  const steadyServices = parseSteadyStateServices(billing)
  const taxMonthly = getTaxMonthlyUsd(misc, bridgeRows)

  let steady = 0
  let usageDriven = 0
  let newOrChanged = 0

  for (const row of bridgeRows) {
    if (isTaxService(row.service)) continue

    const runRate = row.currentRunRateUsd
    const isNewOrChanged =
      levelShiftServices.has(row.service) ||
      newSkuServices.has(row.service) ||
      ((row.partition === 'new_sku' || row.partition === 'grown') &&
        row.deltaUsd >= CLASSIFICATION_MEANINGFUL_DELTA_USD)

    if (isNewOrChanged) {
      newOrChanged += runRate
    } else if (steadyServices.has(row.service) && !levelShiftServices.has(row.service)) {
      steady += runRate
    } else {
      usageDriven += runRate
    }
  }

  if (bridgeRows.length === 0) {
    const runRates = asRecordArray(
      billing?.run_rates && typeof billing.run_rates === 'object'
        ? (billing.run_rates as Record<string, unknown>).by_service
        : null
    )
    for (const row of runRates) {
      const service = asString(row.service)
      const runRate = asNumber(row.run_rate_monthly_usd) ?? 0
      if (isTaxService(service)) continue

      const isNewOrChanged =
        levelShiftServices.has(service) || newSkuServices.has(service)
      if (isNewOrChanged) {
        newOrChanged += runRate
      } else if (steadyServices.has(service) && !levelShiftServices.has(service)) {
        steady += runRate
      } else {
        usageDriven += runRate
      }
    }
  }

  usageDriven = Math.max(0, usageDriven)
  const taxAndCredits = taxMonthly

  return {
    steady,
    usage_driven: usageDriven,
    new_or_changed: newOrChanged,
    tax_and_credits: taxAndCredits,
    total,
  }
}

function sumRunRatesByService(billing: Record<string, unknown> | null): number | null {
  const runRates = billing?.run_rates
  if (!runRates || typeof runRates !== 'object') return null
  const byService = (runRates as Record<string, unknown>).by_service
  if (!Array.isArray(byService)) return null
  let sum = 0
  for (const row of byService) {
    if (!isRecord(row)) continue
    sum += asNumber(row.run_rate_monthly_usd) ?? 0
  }
  return sum
}

/** Largest monthly spend delta from spend_bridge and level_shifts (WS2). */
export function getBiggestChange(billing: Record<string, unknown> | null): BiggestChange | null {
  const bridgeRows = parseSpendBridgeRows(billing)
  let best: BiggestChange | null = null

  for (const row of bridgeRows) {
    if (row.deltaUsd <= 0) continue
    if (!best || row.deltaUsd > best.deltaUsd) {
      best = {
        service: row.service,
        deltaUsd: row.deltaUsd,
        partition: row.partition || null,
        onsetDate: row.onsetDate,
      }
    }
  }

  const raw = billing?.what_changed
  if (raw && typeof raw === 'object') {
    for (const row of asRecordArray((raw as Record<string, unknown>).level_shifts)) {
      const delta = asNumber(row.run_rate_delta_monthly_usd) ?? 0
      if (delta <= 0) continue
      if (!best || delta > best.deltaUsd) {
        best = {
          service: asString(row.service),
          deltaUsd: delta,
          partition: 'level_shift',
          onsetDate: asString(row.onset_date) || null,
        }
      }
    }
  }

  return best
}

/** Run-rate and projected month labels for the overview card. */
export function getRunRateProjection(
  billing: Record<string, unknown> | null,
  mtdProjection: { projected: number } | null
): RunRateProjection | null {
  if (!billing) return null

  const bridgeRaw = billing.spend_bridge
  const bridge =
    bridgeRaw && typeof bridgeRaw === 'object'
      ? (bridgeRaw as Record<string, unknown>)
      : null

  const bridgeRunRate = asNumber(bridge?.current_run_rate_total_usd)
  const summedRunRates = sumRunRatesByService(billing)

  let runRateMonthly = 0
  let runRateLabel = '14-day trailing run rate'
  if (bridgeRunRate != null) {
    runRateMonthly = bridgeRunRate
    runRateLabel = '14-day trailing run rate (spend bridge)'
  } else if (summedRunRates != null) {
    runRateMonthly = summedRunRates
    runRateLabel = '14-day trailing run rate (by service)'
  } else {
    return null
  }

  const backendProjected = asNumber(billing.projected_current_month)
  let projectedMonth: number | null = null
  let projectedLabel: string | null = null

  if (backendProjected != null) {
    projectedMonth = backendProjected
    projectedLabel = 'projected current month (billing daily trend)'
  } else if (mtdProjection) {
    projectedMonth = mtdProjection.projected
    projectedLabel = 'projected current month (MTD extrapolation)'
  }

  return {
    runRateMonthly,
    projectedMonth,
    runRateLabel,
    projectedLabel,
  }
}

export interface MonthlyByServiceRow {
  month: string
  service: string
  total_cost?: number
  total_credits?: number
  net_cost: number
}

export interface DailyTrendRow {
  date: string
  net_cost: number
}

export interface DailyAnomalyRow {
  date: string
  service: string
  cost: number
  baseline_avg: number
  ratio: number
}

export interface TopResourceRow {
  service: string
  resource_name: string
  net_cost: number
}

export interface GlobalSkuRow {
  service: string
  sku: string
  net_cost: number
  usage_amount?: number
  usage_unit?: string
}

export interface CreditsBreakdownRow {
  month: string
  credit_type: string
  credit_name: string
  credit_amount: number
}

export interface TopSpendRow {
  id: string
  service: string
  description: string
  sku: string
  estimatedMonthlyCost: number
  pctOfTotal: number
  trend: number | null
  anchorId: string | null
}

export interface RuleBasedRecommendation {
  type?: string
  service?: string
  instance?: string
  bucket?: string
  resource?: string
  current?: string
  suggested?: string
  reason?: string
  risk?: string
  estimated_savings?: string
  implementation?: string
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function asRecordArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return []
  return value.filter(isRecord)
}

export function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

export function asString(value: unknown): string {
  if (typeof value === 'string') return value
  if (value == null) return ''
  return String(value)
}

export function getSectionData(
  analysis: GcpAnalysisResult | null | undefined,
  key: GcpAnalyzerSectionKey
): Record<string, unknown> | null {
  const section = analysis?.sections?.[key]
  if (!section || isGcpSectionError(section)) return null
  return section as Record<string, unknown>
}

export function parseMonthlyByService(section: GcpSectionData | undefined): MonthlyByServiceRow[] {
  if (!section || isGcpSectionError(section)) return []
  return asRecordArray(section.monthly_by_service)
    .map((row) => ({
      month: asString(row.month),
      service: asString(row.service),
      total_cost: asNumber(row.total_cost) ?? undefined,
      total_credits: asNumber(row.total_credits) ?? undefined,
      net_cost: asNumber(row.net_cost) ?? 0,
    }))
    .filter((row) => row.month && row.service)
}

/** Mirrors `complete_months` / `_latest_monthly_total` in the backend. */
export function getLatestMonthlyTotal(
  monthlyRows: MonthlyByServiceRow[],
  today: Date = new Date()
): { month: string; total: number } {
  if (monthlyRows.length === 0) {
    return { month: 'unknown', total: 0 }
  }

  const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`

  const totals = new Map<string, number>()
  for (const row of monthlyRows) {
    if (row.month >= currentMonthKey) continue
    totals.set(row.month, (totals.get(row.month) ?? 0) + row.net_cost)
  }

  const sortedMonths = [...totals.keys()].sort((a, b) => b.localeCompare(a))
  const latestMonth = sortedMonths[0] ?? 'unknown'
  return { month: latestMonth, total: totals.get(latestMonth) ?? 0 }
}

export function getCompleteMonthKeys(
  monthlyRows: MonthlyByServiceRow[],
  today: Date = new Date()
): string[] {
  const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
  return [...new Set(monthlyRows.map((row) => row.month))]
    .filter((month) => month < currentMonthKey)
    .sort((a, b) => a.localeCompare(b))
}

export function formatMonthLabel(monthKey: string): string {
  if (monthKey === 'unknown') return 'Unknown month'
  const [year, month] = monthKey.split('-')
  if (!year || !month) return monthKey
  const date = new Date(Number(year), Number(month) - 1, 1)
  if (Number.isNaN(date.getTime())) return monthKey
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric' })
}

const CLOUD_RUN_BILLING_MODE_SKU_MARKERS = [
  'instance-based',
  'min instance',
  'min-instance',
  'minimum instance',
] as const

/** Billing what_changed new SKUs that indicate a Cloud Run billing-mode shift. */
export function filterCloudRunBillingModeNewSkus(newSkus: NewSkuRow[]): NewSkuRow[] {
  return newSkus.filter((row) => {
    if (!row.service.toLowerCase().includes('cloud run')) return false
    const skuLower = row.sku.toLowerCase()
    return CLOUD_RUN_BILLING_MODE_SKU_MARKERS.some((marker) => skuLower.includes(marker))
  })
}

export function formatSqlConnectionPath(
  ipv4Enabled: boolean | undefined,
  privateNetwork: string | null | undefined
): string {
  const hasPrivate = Boolean(privateNetwork && privateNetwork.trim())
  if (hasPrivate) return 'Private IP'
  if (ipv4Enabled) return 'Public IP'
  return 'Unknown'
}

export function computeMtdProjection(dailyRows: DailyTrendRow[]): {
  mtdSpend: number
  projected: number
  daysElapsed: number
  daysInMonth: number
} | null {
  if (dailyRows.length === 0) return null

  const now = new Date()
  const currentMonthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  const monthRows = dailyRows.filter((row) => row.date.startsWith(currentMonthPrefix))
  if (monthRows.length === 0) return null

  const mtdSpend = monthRows.reduce((sum, row) => sum + row.net_cost, 0)
  const daysElapsed = monthRows.length
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
  const projected = daysElapsed > 0 ? (mtdSpend / daysElapsed) * daysInMonth : mtdSpend

  return { mtdSpend, projected, daysElapsed, daysInMonth }
}

export function computeOverallMomChange(
  monthlyRows: MonthlyByServiceRow[],
  today: Date = new Date()
): { change: number; changePct: number; previousMonth: string; latestMonth: string } | null {
  const completeMonths = getCompleteMonthKeys(monthlyRows, today)
  if (completeMonths.length < 2) return null

  const latestMonth = completeMonths[completeMonths.length - 1]!
  const previousMonth = completeMonths[completeMonths.length - 2]!

  const totals = new Map<string, number>()
  for (const row of monthlyRows) {
    totals.set(row.month, (totals.get(row.month) ?? 0) + row.net_cost)
  }

  const latestTotal = totals.get(latestMonth) ?? 0
  const previousTotal = totals.get(previousMonth) ?? 0
  const change = latestTotal - previousTotal
  const changePct = previousTotal !== 0 ? (change / previousTotal) * 100 : 0

  return { change, changePct, previousMonth, latestMonth }
}

export function serviceToSectionAnchor(serviceName: string): string | null {
  const normalized = serviceName.toLowerCase()
  if (normalized.includes('bigquery')) return 'bigquery'
  if (normalized.includes('cloud run')) return 'cloud_run'
  if (normalized.includes('cloud sql')) return 'cloud_sql'
  if (normalized.includes('cloud storage') || normalized.includes('storage')) return 'gcs'
  if (normalized.includes('compute') || normalized.includes('network')) return 'compute'
  return null
}

export function buildServiceSummaryRows(
  monthlyRows: MonthlyByServiceRow[],
  latestMonth: string,
  latestTotal: number
): Array<{
  service: string
  latestCost: number
  change: number | null
  changePct: number | null
  pctOfTotal: number
  anchorId: string | null
}> {
  const previousMonths = [...new Set(monthlyRows.map((r) => r.month))]
    .sort((a, b) => b.localeCompare(a))
    .filter((m) => m !== latestMonth)
  const previousMonth = previousMonths[0]

  const latestByService = new Map<string, number>()
  const previousByService = new Map<string, number>()

  for (const row of monthlyRows) {
    if (row.month === latestMonth) {
      latestByService.set(row.service, (latestByService.get(row.service) ?? 0) + row.net_cost)
    }
    if (previousMonth && row.month === previousMonth) {
      previousByService.set(row.service, (previousByService.get(row.service) ?? 0) + row.net_cost)
    }
  }

  return [...latestByService.entries()]
    .map(([service, latestCost]) => {
      const prev = previousByService.get(service)
      const change = prev != null ? latestCost - prev : null
      const changePct = prev != null && prev !== 0 ? ((latestCost - prev) / prev) * 100 : null
      return {
        service,
        latestCost,
        change,
        changePct,
        pctOfTotal: latestTotal > 0 ? (latestCost / latestTotal) * 100 : 0,
        anchorId: serviceToSectionAnchor(service),
      }
    })
    .sort((a, b) => b.latestCost - a.latestCost)
}

export function buildTopSpendRows(
  billingSection: Record<string, unknown> | null,
  analysisMonths: number
): TopSpendRow[] {
  if (!billingSection) return []

  const months = Math.max(analysisMonths, 1)
  const monthlyRows = parseMonthlyByService(billingSection)
  const { total: latestTotal } = getLatestMonthlyTotal(monthlyRows)

  const trendByService = new Map<string, number>()
  const serviceRows = buildServiceSummaryRows(
    monthlyRows,
    getLatestMonthlyTotal(monthlyRows).month,
    latestTotal
  )
  for (const row of serviceRows) {
    if (row.changePct != null) trendByService.set(row.service, row.changePct)
  }

  const rowMap = new Map<string, TopSpendRow>()

  const addRow = (service: string, description: string, sku: string, windowCost: number) => {
    const estimatedMonthlyCost = windowCost / months
    const id = `${service}::${description}`
    const existing = rowMap.get(id)
    if (existing && existing.estimatedMonthlyCost >= estimatedMonthlyCost) return

    rowMap.set(id, {
      id,
      service,
      description,
      sku,
      estimatedMonthlyCost,
      pctOfTotal: latestTotal > 0 ? (estimatedMonthlyCost / latestTotal) * 100 : 0,
      trend: trendByService.get(service) ?? null,
      anchorId: serviceToSectionAnchor(service),
    })
  }

  for (const row of asRecordArray(billingSection.top_resources)) {
    const service = asString(row.service)
    const resource = asString(row.resource_name)
    const cost = asNumber(row.net_cost) ?? 0
    if (!service || cost <= 0) continue
    addRow(service, resource || 'Unknown resource', resource, cost)
  }

  for (const row of asRecordArray(billingSection.global_skus)) {
    const service = asString(row.service)
    const sku = asString(row.sku)
    const cost = asNumber(row.net_cost) ?? 0
    if (!service || !sku || cost <= 0) continue
    addRow(service, sku, sku, cost)
  }

  return [...rowMap.values()].sort((a, b) => b.estimatedMonthlyCost - a.estimatedMonthlyCost)
}

export function getBiggestLineItem(
  billingSection: Record<string, unknown> | null,
  analysisMonths: number
): { service: string; resource: string; monthlyCost: number } | null {
  const rows = buildTopSpendRows(billingSection, analysisMonths)
  if (rows.length === 0) return null
  const top = rows[0]!
  return {
    service: top.service,
    resource: top.description,
    monthlyCost: top.estimatedMonthlyCost,
  }
}

export function getQueryOptimizationHint(
  group: GcpRecurringQueryGroup,
  unpartitionedTables: string[]
): string {
  const refs = group.top_referenced_tables ?? []
  for (const ref of refs) {
    const match = unpartitionedTables.find(
      (table) => ref.includes(table) || table.includes(ref)
    )
    if (match) {
      return `This query scans a full unpartitioned table each run — partitioning ${match} by date could cut its cost up to ~90%.`
    }
  }

  if ((group.cache_hit_pct ?? 100) < 20 && (group.run_count ?? 0) > 5) {
    return 'Low cache hit rate — similar queries are re-scanning data. Consider materialized views or scheduled tables.'
  }

  if ((group.avg_cost_per_run ?? 0) > 1) {
    return 'High cost per run — review filters, selected columns, and whether a smaller date range would work.'
  }

  return 'Review the query for unnecessary full-table scans and consider clustering or partitioning on frequently filtered columns.'
}

export function getSlotUtilizationSummary(slotData: Record<string, unknown> | null): string | null {
  if (!slotData) return null
  const recommendation = asString(slotData.flat_rate_recommendation)
  if (recommendation.includes('cheaper') || recommendation.includes('On-demand')) {
    return "You're far below the point where reserved slots would save money."
  }
  if (recommendation.includes('save')) {
    return 'Your usage may be high enough that reserved slots could save money — review flat-rate pricing.'
  }
  return recommendation || null
}

export function lookupSkuExplanation(skuDescription: string): string | null {
  const normalized = skuDescription.trim()
  if (!normalized) return null
  for (const entry of SKU_GLOSSARY) {
    if (normalized.toLowerCase().includes(entry.match.toLowerCase())) {
      return entry.explanation
    }
  }
  return null
}

function sectionHasError(
  analysis: GcpAnalysisResult | null | undefined,
  key: GcpAnalyzerSectionKey
): boolean {
  if (!analysis?.sections) return false
  const section = analysis.sections[key]
  return isGcpSectionError(section)
}

export type StepperRowStatus = 'pending' | 'running' | 'done' | 'failed'

export function getStepperRowStatus(
  stepIndex: number,
  processedItems: number,
  jobStatus: 'pending' | 'running' | 'completed' | 'failed' | 'idle',
  analysis: GcpAnalysisResult | null
): StepperRowStatus {
  const step = ANALYZER_STEPS[stepIndex]
  if (!step) return 'pending'

  if (sectionHasError(analysis, step.key)) {
    return 'failed'
  }

  // Prefer the analysis payload when present — the background-job row can leave
  // the poller store after completion, which would zero out processedItems.
  if (analysis?.sections && Object.prototype.hasOwnProperty.call(analysis.sections, step.key)) {
    return 'done'
  }

  const isJobActive = jobStatus === 'pending' || jobStatus === 'running'
  const isJobFinished = jobStatus === 'completed' || jobStatus === 'failed'

  if (isJobFinished) {
    if (processedItems >= ANALYZER_STEPS.length) return 'done'
    return processedItems > stepIndex ? 'done' : 'pending'
  }

  if (processedItems > stepIndex) {
    return 'done'
  }

  if (isJobActive && processedItems === stepIndex) {
    return 'running'
  }

  return 'pending'
}

export function getSectionErrorMessage(
  analysis: GcpAnalysisResult | null,
  key: GcpAnalyzerSectionKey
): string | null {
  const section = analysis?.sections?.[key]
  if (!isGcpSectionError(section)) return null
  if (section.missing_role_hint) {
    return `${section.error} (missing role: ${section.missing_role_hint})`
  }
  return section.error
}

export function buildReportMarkdown(
  analysis: GcpAnalysisResult | null,
  recommendations: GcpRecommendationsResult | null
): string {
  const lines: string[] = ['# GCP Cost Analysis Report', '']

  if (analysis?._meta) {
    lines.push('## Analysis metadata')
    lines.push(`- Project: ${analysis._meta.project_id}`)
    lines.push(`- Billing months: ${analysis._meta.analysis_months}`)
    lines.push(`- Metrics days: ${analysis._meta.metrics_days}`)
    lines.push(`- Generated: ${analysis._meta.analysis_date}`)
    lines.push('')
  }

  if (analysis?.sections) {
    lines.push('## Analysis sections')
    for (const step of ANALYZER_STEPS) {
      const section = analysis.sections[step.key]
      lines.push(`### ${step.label}`)
      if (!section) {
        lines.push('_No data_')
      } else if (isGcpSectionError(section)) {
        lines.push(`**Error:** ${section.error}`)
        if (section.missing_role_hint) {
          lines.push(`**Missing role:** ${section.missing_role_hint}`)
        }
      } else {
        lines.push('```json')
        lines.push(JSON.stringify(section, null, 2))
        lines.push('```')
      }
      lines.push('')
    }
  }

  if (recommendations) {
    lines.push('## AI recommendations')
    lines.push(recommendations.summary)
    lines.push('')
    lines.push(
      `Latest monthly spend: ${formatUsd(recommendations.latest_monthly_total_usd)}`
    )
    lines.push(
      `Total potential savings: ${formatUsd(recommendations.total_potential_savings_usd_monthly)}/mo`
    )
    lines.push('')

    if (recommendations.quick_wins.length > 0) {
      lines.push('### Quick wins')
      for (const win of recommendations.quick_wins) {
        lines.push(
          `- **${win.title}** (${formatUsd(win.estimated_savings_usd_monthly)}/mo, ${win.effort})`
        )
        lines.push('  ```')
        lines.push(`  ${win.command}`)
        lines.push('  ```')
      }
      lines.push('')
    }

    if (recommendations.recommendations.length > 0) {
      lines.push('### Ranked recommendations')
      const sorted = [...recommendations.recommendations].sort(
        (a, b) => a.priority - b.priority
      )
      for (const rec of sorted) {
        lines.push(`#### ${rec.priority}. ${rec.service}${rec.resource ? ` — ${rec.resource}` : ''}`)
        lines.push(`- Risk: ${rec.risk} | Effort: ${rec.effort}`)
        lines.push(`- Savings: ${formatUsd(rec.estimated_savings_usd_monthly)}/mo`)
        lines.push(`- Issue: ${rec.issue}`)
        lines.push(`- Recommendation: ${rec.recommendation}`)
        lines.push('```')
        lines.push(rec.implementation)
        lines.push('```')
        lines.push(`- Verification: ${rec.verification}`)
        lines.push(`- Data citation: ${rec.data_citation}`)
        lines.push('')
      }
    }

    if (recommendations.additional_data_needed.length > 0) {
      lines.push('### Additional data needed')
      for (const item of recommendations.additional_data_needed) {
        lines.push(`- **${item.data_type}**: ${item.reason} (${item.how_to_get})`)
      }
    }
  }

  return lines.join('\n')
}
